import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useInstanceDetails } from '@/hooks/useInstances'
import { useEntityFacets } from '@/hooks/useFacetGraph'
import { LinkedFacetsList } from '@/components/sources/LinkedFacetCard'
import { getLocalName } from '@/lib/ontology/namespaces'
import { ArrowLeft, ArrowRight, ExternalLink, Loader2, Trash2, FileText, ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'

interface InstanceDetailProps {
  instanceUri: string
  onNavigate?: (uri: string) => void
  onDelete?: (uri: string) => void
}

export function InstanceDetail({ instanceUri, onNavigate, onDelete }: InstanceDetailProps) {
  const { data, isLoading, error } = useInstanceDetails(instanceUri)
  const { facets, count: facetCount, isLoading: facetsLoading, unlinkFacet, isUnlinking } = useEntityFacets(instanceUri)
  const [facetsExpanded, setFacetsExpanded] = useState(true)

  if (isLoading) {
    return (
      <Card className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
      </Card>
    )
  }

  if (error || !data) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="text-solution">Error</CardTitle>
          <CardDescription>Failed to load instance details</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card className="h-full overflow-auto">
      <CardHeader className="border-b border-border">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <CardTitle className="font-mono text-lg break-all">
              {getLocalName(instanceUri)}
            </CardTitle>
            <CardDescription className="font-mono text-xs mt-1 break-all">
              {instanceUri}
            </CardDescription>
          </div>
          {onDelete && (
            <Button
              variant="ghost"
              size="icon"
              className="text-solution hover:text-solution hover:bg-solution/10"
              onClick={() => onDelete(instanceUri)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {data.types.map((type) => (
            <Badge key={type.uri} variant="outline">
              {type.label}
            </Badge>
          ))}
        </div>
      </CardHeader>

      <CardContent className="space-y-6 pt-4">
        {/* Data Properties */}
        {Object.keys(data.properties).length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-text mb-2">Properties</h4>
            <div className="bg-background rounded-lg p-3 space-y-2">
              {Object.entries(data.properties).map(([propUri, values]) => (
                <div key={propUri} className="flex items-start gap-2 py-1 border-b border-border last:border-0">
                  <span className="text-xs font-mono text-text-muted min-w-[120px]">
                    {getLocalName(propUri)}
                  </span>
                  <div className="flex-1">
                    {values.map((v, i) => (
                      <div key={i} className="text-sm">
                        {v.value.startsWith('http') ? (
                          <button
                            onClick={() => onNavigate?.(v.value)}
                            className="text-primary hover:underline flex items-center gap-1"
                          >
                            {v.label || getLocalName(v.value)}
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        ) : (
                          <span>{v.value}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Outgoing Relationships */}
        {data.outgoing.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-text mb-2 flex items-center gap-1">
              <ArrowRight className="w-4 h-4 text-business" />
              Outgoing Relationships
            </h4>
            <div className="bg-background rounded-lg divide-y divide-border">
              {data.outgoing.map((rel, i) => (
                <div key={i} className="flex items-center gap-2 p-2">
                  <Badge variant="business" className="text-xs">
                    {getLocalName(rel.property)}
                  </Badge>
                  <ArrowRight className="w-3 h-3 text-text-muted" />
                  <button
                    onClick={() => onNavigate?.(rel.target)}
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    {rel.targetLabel}
                    <span className="text-xs text-text-muted">
                      ({getLocalName(rel.targetType)})
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Incoming Relationships */}
        {data.incoming.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-text mb-2 flex items-center gap-1">
              <ArrowLeft className="w-4 h-4 text-market" />
              Incoming Relationships
            </h4>
            <div className="bg-background rounded-lg divide-y divide-border">
              {data.incoming.map((rel, i) => (
                <div key={i} className="flex items-center gap-2 p-2">
                  <button
                    onClick={() => onNavigate?.(rel.source)}
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    {rel.sourceLabel}
                    <span className="text-xs text-text-muted">
                      ({getLocalName(rel.sourceType)})
                    </span>
                  </button>
                  <ArrowRight className="w-3 h-3 text-text-muted" />
                  <Badge variant="market" className="text-xs">
                    {getLocalName(rel.property)}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Linked Facets - Evidence from Sources */}
        <div>
          <button
            onClick={() => setFacetsExpanded(!facetsExpanded)}
            className="w-full flex items-center justify-between text-sm font-medium text-text mb-2 hover:text-primary transition-colors"
          >
            <div className="flex items-center gap-1">
              <FileText className="w-4 h-4 text-solution" />
              <span>Linked Facets</span>
              {facetCount > 0 && (
                <Badge variant="outline" className="ml-1 text-xs">
                  {facetCount}
                </Badge>
              )}
            </div>
            {facetsExpanded ? (
              <ChevronDown className="w-4 h-4 text-text-muted" />
            ) : (
              <ChevronRight className="w-4 h-4 text-text-muted" />
            )}
          </button>

          {facetsExpanded && (
            <div className="bg-background rounded-lg p-3">
              {facetsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-4 h-4 animate-spin text-text-muted" />
                </div>
              ) : (
                <LinkedFacetsList
                  facets={facets}
                  onUnlink={(facetUri) => {
                    // Extract sourceId and facetId from the facetUri for the API call
                    // The unlinkFacet mutation needs sourceId and facetId
                    unlinkFacet({ sourceId: '', facetId: facetUri, entityUri: instanceUri })
                  }}
                  isUnlinking={isUnlinking}
                  emptyMessage="No facets linked to this entity yet"
                  compact
                  maxVisible={5}
                />
              )}
            </div>
          )}
        </div>

        {/* No data message */}
        {Object.keys(data.properties).length === 0 &&
          data.outgoing.length === 0 &&
          data.incoming.length === 0 &&
          facetCount === 0 && (
            <p className="text-sm text-text-muted italic text-center py-4">
              No properties, relationships, or linked facets found for this instance.
            </p>
          )}
      </CardContent>
    </Card>
  )
}
